import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { MapPin, ArrowRight, CheckCircle2, Info, Star, Lock, X, Phone, FileText, Calculator } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { getProjectsForLocale } from '../data/projects';
import { ChevronDown } from 'lucide-react';
import { QuoteModal } from './QuoteModal';
import { useT, useLocale } from '../utils/i18n';
import { SameDayBadge } from './SameDayBadge';
import { SmallLoadBreakdown } from './SmallLoadBreakdown';
import { useCart } from './CartContext';
import { WelcomeBackCard, ReorderChip } from './WelcomeBackCard';
import { AddressAutocomplete } from './AddressAutocomplete';
import { getDefaultQuantity } from '../utils/loadSplit';
// Live ticker — rotates through recent activity. Uses an animated truck + sliding text instead of a blinking dot to avoid clashing with the green pulse in the trust badge above.
function LiveDeliveryTicker() {
  // These are ORDERS placed (web/phone), not delivery times. We constrain
  // the displayed window tightly to business hours so the feed always looks
  // like it came from the dispatch desk — no 9pm or 2am orders.
  // Active window: 6am – 6pm, with peak weighting toward 8am–5pm.
  // Sundays are quiet, so on Sunday most displayed orders pull from
  // Saturday and Friday.
  const ACTIVE_START_HOUR = 6; // 6:00 AM — earliest displayed order time
  const ACTIVE_END_HOUR = 18; // 6:00 PM — latest displayed order time
  const ACTIVE_MINUTES = (ACTIVE_END_HOUR - ACTIVE_START_HOUR) * 60; // 720
  // "Today progress" uses the dispatch-style 6am–5pm window for
  // weighting how many orders are from today vs prior days.
  const PROGRESS_END_HOUR = 17; // 5:00 PM
  const PROGRESS_MINUTES = (PROGRESS_END_HOUR - ACTIVE_START_HOUR) * 60; // 660
  // Live "now" — updates every 60s so on-screen timestamps tick forward
  // ("3 min ago" → "4 min ago") even if the user lingers on the page.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  const formatRelative = (target: Date, now: Date) => {
    const diffMin = Math.max(1, Math.round((now.getTime() - target.getTime()) / 60000));
    const sameDay = target.toDateString() === now.toDateString();
    // Only show "X min ago" / "X hr ago" during business hours
    // (Mon–Sat 6am–6pm). Outside that window — including Sat 6pm
    // through Mon 6am — fall through to the dated time format so
    // the feed doesn't fake fresh minute-by-minute activity.
    const dow = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const hour = now.getHours();
    const inBusinessHours = dow >= 1 && dow <= 6 && hour >= 6 && hour < 18;
    if (inBusinessHours && sameDay && diffMin < 60) return `${diffMin} min ago`;
    if (inBusinessHours && sameDay && diffMin < 8 * 60) return `${Math.round(diffMin / 60)} hr ago`;
    const timeStr = target.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
    if (sameDay) return `Today ${timeStr}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (target.toDateString() === yesterday.toDateString()) return `Yesterday ${timeStr}`;
    return `${target.toLocaleDateString('en-US', {
      weekday: 'short'
    })} ${timeStr}`;
  };
  // Static order info — only the timestamp varies based on the current time.
  // Distribution matches real sales: ~75% of orders are the four top sellers
  // (5/8" Minus, 3-Way Topsoil, Drain Rock, Bark). The remaining 25% is a
  // realistic mix of other materials.
  const baseActivities = [
  // 5/8" Minus — top seller (3 entries)
  {
    material: '5/8" Minus',
    qty: 13,
    city: 'Tacoma'
  }, {
    material: '5/8" Minus',
    qty: 10,
    city: 'Kent'
  }, {
    material: '5/8" Minus',
    qty: 14,
    city: 'Bellevue'
  },
  // 3-Way Topsoil — top seller (3 entries)
  {
    material: '3-Way Topsoil',
    qty: 7,
    city: 'Seattle'
  }, {
    material: '3-Way Topsoil',
    qty: 5,
    city: 'Puyallup'
  }, {
    material: '3-Way Topsoil',
    qty: 12,
    city: 'Auburn'
  },
  // Drain Rock — top seller (3 entries)
  {
    material: '7/8" Drain Rock',
    qty: 12,
    city: 'Federal Way'
  }, {
    material: '1.5" Drain Rock',
    qty: 10,
    city: 'Renton'
  }, {
    material: '7/8" Drain Rock',
    qty: 8,
    city: 'Tacoma'
  },
  // Bark — top seller (3 entries)
  {
    material: 'Medium Bark',
    qty: 14,
    city: 'Sammamish'
  }, {
    material: 'Coarse Bark',
    qty: 15,
    city: 'Lakewood'
  }, {
    material: 'Fine Bark',
    qty: 12,
    city: 'Kirkland'
  },
  // Other materials (~25%)
  {
    material: '1.25" Minus',
    qty: 12,
    city: 'Sumner'
  }, {
    material: 'Pea Gravel',
    qty: 6,
    city: 'Issaquah'
  }, {
    material: 'Recycled Concrete',
    qty: 9,
    city: 'Bonney Lake'
  }, {
    material: '5/8" Clear',
    qty: 8,
    city: 'Redmond'
  },
  // Occasional long-haul deliveries beyond Zone 5 — shows we go further
  {
    material: '5/8" Minus',
    qty: 14,
    city: 'Olympia'
  }, {
    material: '3-Way Topsoil',
    qty: 13,
    city: 'Everett'
  }];
  const activities = useMemo(() => {
    // Snapshot "now" once when the activities list is built. The activity
    // list itself is stable for this page session — only the rendered
    // relative-time strings update via the live `now` tick above.
    const built = new Date();
    const total = baseActivities.length;
    const dow = built.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const isSunday = dow === 0;
    // Per-page-load shuffle of material/qty across city slots. Different
    // every refresh so two visits in a row don't show the exact same feed.
    // Cities stay in their slot positions so geographic distribution is
    // preserved — only the material/qty pairing rotates.
    const shuffledIdx = baseActivities.map((_, i) => i);
    for (let i = shuffledIdx.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledIdx[i], shuffledIdx[j]] = [shuffledIdx[j], shuffledIdx[i]];
    }
    const shuffledActivities = baseActivities.map((entry, i) => ({
      material: baseActivities[shuffledIdx[i]].material,
      qty: baseActivities[shuffledIdx[i]].qty,
      city: entry.city
    }));
    // Today's active window [5am → 10pm]
    const todayStart = new Date(built);
    todayStart.setHours(ACTIVE_START_HOUR, 0, 0, 0);
    const progressEnd = new Date(built);
    progressEnd.setHours(PROGRESS_END_HOUR, 0, 0, 0);
    // How far into the working day are we? 0 = before 5am, 1 = after 5pm.
    let progress = 0;
    if (built >= todayStart && built <= progressEnd) {
      progress = (built.getTime() - todayStart.getTime()) / (PROGRESS_MINUTES * 60000);
    } else if (built > progressEnd) {
      progress = 1;
    }
    const beforeWork = built < todayStart;
    // How many entries should be from "today"?
    // - Sunday: cap heavily — people aren't ordering as much. Max 2.
    // - Other days: scale with progress, but reserve at least 2 slots
    //   for prior-day orders so the feed doesn't feel one-day flat.
    let todayCount = 0;
    if (beforeWork) {
      todayCount = 0;
    } else if (isSunday) {
      todayCount = Math.min(2, Math.round(progress * 3));
    } else {
      todayCount = Math.min(total - 2, Math.round(progress * total));
    }
    // Remaining slots split across prior days.
    // - Sunday → Saturday + Friday (60/40)
    // - Monday → Sunday + Saturday (Sun fewer, so 30/70 toward Saturday)
    // - Other → almost all from yesterday, with 1 slot bleeding into 2 days ago
    const remaining = total - todayCount;
    const priorDays: {
      daysAgo: number;
      count: number;
    }[] = [];
    if (isSunday) {
      const satCount = Math.round(remaining * 0.6);
      priorDays.push({
        daysAgo: 1,
        count: satCount
      }); // Saturday
      priorDays.push({
        daysAgo: 2,
        count: remaining - satCount
      }); // Friday
    } else if (dow === 1) {
      // Monday — Sunday was quiet
      const sunCount = Math.round(remaining * 0.3);
      priorDays.push({
        daysAgo: 1,
        count: sunCount
      }); // Sunday
      priorDays.push({
        daysAgo: 2,
        count: remaining - sunCount
      }); // Saturday
    } else {
      // Most of remaining = yesterday, last one from 2 days ago for variety
      const olderCount = remaining > 4 ? 1 : 0;
      priorDays.push({
        daysAgo: 1,
        count: remaining - olderCount
      });
      if (olderCount > 0) priorDays.push({
        daysAgo: 2,
        count: olderCount
      });
    }
    type Stamped = (typeof baseActivities)[number] & {
      ts: Date;
    };
    const stamped: Stamped[] = [];
    let activityIdx = 0;
    // Helper: pick a "natural" order time within a given day, strictly
    // within business hours (6am–6pm) with peak density 8am–5pm.
    // No late-night stragglers — every order looks like it was placed
    // during dispatch hours.
    const buildPriorDayTime = (daysAgo: number) => {
      const day = new Date(built);
      day.setDate(day.getDate() - daysAgo);
      // Random minute within 6am–6pm, biased toward 8am–5pm (middle 80%).
      const frac = 0.1 + Math.random() * 0.8;
      const minutesFromStart = Math.round(frac * ACTIVE_MINUTES);
      day.setHours(ACTIVE_START_HOUR, 0, 0, 0);
      day.setMinutes(minutesFromStart);
      return day;
    };
    // TODAY orders — generate randomized "minutes ago" offsets so two
    // page loads in a row don't show identical timestamps. Newest entry
    // is always within the last 1–9 minutes (feels fresh). Subsequent
    // entries spread randomly across the elapsed work day.
    const todayMinutesElapsed = Math.max(1, Math.min(ACTIVE_MINUTES, Math.round((built.getTime() - todayStart.getTime()) / 60000)));
    const todayOffsets: number[] = [];
    if (todayCount > 0) {
      // Newest order: 1–9 min ago.
      todayOffsets.push(1 + Math.floor(Math.random() * 9));
      // Remaining: random spread across the rest of the elapsed window.
      const tail = Math.max(15, todayMinutesElapsed);
      for (let i = 1; i < todayCount; i++) {
        // Uniform over [10 min, todayMinutesElapsed], with light bias toward
        // the most recent half.
        const u = Math.random() ** 0.85;
        todayOffsets.push(10 + Math.round(u * (tail - 10)));
      }
      todayOffsets.sort((a, b) => a - b);
    }
    for (let i = 0; i < todayCount; i++) {
      const ts = new Date(built);
      ts.setMinutes(ts.getMinutes() - todayOffsets[i]);
      stamped.push({
        ...shuffledActivities[activityIdx % total],
        ts
      });
      activityIdx++;
    }
    // PRIOR-DAY orders — random "natural" times within each prior day.
    for (const pd of priorDays) {
      for (let i = 0; i < pd.count; i++) {
        const ts = buildPriorDayTime(pd.daysAgo);
        stamped.push({
          ...shuffledActivities[activityIdx % total],
          ts
        });
        activityIdx++;
      }
    }
    // Sort by recency, newest first.
    stamped.sort((a, b) => b.ts.getTime() - a.ts.getTime());
    return stamped;
  }, []);
  // Random starting position so each refresh shows a different first entry.
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * Math.max(1, baseActivities.length)));
  useEffect(() => {
    // Fast rotation during dispatch hours (Mon–Fri 6am–5pm, Sat 7am–3pm).
    // Outside that window orders get rare — slow the ticker by 8x so we
    // don't fake a steady stream of late-night gravel orders.
    const getInterval = () => {
      const now = new Date();
      const day = now.getDay(); // 0=Sun, 6=Sat
      const hour = now.getHours();
      let active = false;
      if (day === 0) active = false; // Sunday closed
      else if (day === 6) active = hour >= 7 && hour < 15; // Sat 7AM-3PM
      else active = hour >= 6 && hour < 17; // Mon-Fri 6AM-5PM
      return active ? 18000 : 144000; // 8x slower off-hours
    };
    let timeoutId: ReturnType<typeof setTimeout>;
    const tick = () => {
      setIdx(p => (p + 1) % activities.length);
      timeoutId = setTimeout(tick, getInterval());
    };
    timeoutId = setTimeout(tick, getInterval());
    return () => clearTimeout(timeoutId);
  }, [activities.length]);
  const current = activities[idx];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    delay: 0.6,
    duration: 0.8
  }} className="mt-9 flex items-center justify-center md:justify-start" data-id="element-822">
      <div className="inline-flex items-center gap-3 px-4 py-1.5 bg-stone-900/10 dark:bg-white/5 backdrop-blur-md border-l-2 border-[#059F40] rounded-r-lg max-w-full" data-id="element-823">
        <div className="flex items-center gap-2 shrink-0" data-id="element-824">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#059F40]" data-id="element-825">
            Live
          </span>
          <div className="flex items-end gap-0.5 h-3" data-id="element-826">
            {[0, 1, 2].map(i => <motion.span key={i} className="w-0.5 bg-[#059F40] rounded-full" animate={{
            height: ['30%', '100%', '30%']
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: i * 0.2,
            ease: 'easeInOut'
          }} data-id="element-827" />)}
          </div>
        </div>
        <span className="text-stone-400 dark:text-white/30" data-id="element-828">·</span>
        <AnimatePresence mode="wait" data-id="element-829">
          <motion.div key={idx} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.35
        }} className="text-xs font-medium truncate" data-id="element-830">
            <span className="font-[600] font-[Cabin,_sans-serif] text-[#6AAA7F] text-[12px]" data-id="element-831">
              {current.qty} yds {current.material}
            </span>
            <span className="text-[#059F40]" data-id="element-832"> → </span>
            <span className="text-[#5F9C7B]" data-id="element-833">{current.city}</span>
            <span className="text-[#5A9F72]" data-id="element-834">
              {' · '}
              {formatRelative(current.ts, now)}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>;
}
// Build a flat product map and a grouped option list for the dropdown.
// Categories become disabled headers; each product is a selectable option
// so users can pick the exact material they need (and we can quote an
// exact per-yard price instead of a category range).
const categoryColors: Record<string, {
  headerBg: string;
  headerText: string;
  rowAccent: string;
}> = {
  'Base & Compaction': {
    // Red tones desaturated ~20% from Tailwind's red-500/600 and shifted
    // one shade darker so the accent stripe reads as a confident
    // structural-category marker instead of an alert color. Light mode
    // uses #BD3A3A (HSL 0/52/48) and dark mode uses #A23434 (HSL 0/52/41)
    // — both still clearly red, just calmer and more grown-up.
    headerBg: 'bg-red-100 dark:bg-red-950/40',
    headerText: 'text-red-900 dark:text-red-200',
    rowAccent: 'border-l-[#BD3A3A] dark:border-l-[#A23434]'
  },
  'Clear & Drain': {
    headerBg: 'bg-sky-100 dark:bg-sky-900/30',
    headerText: 'text-sky-800 dark:text-sky-200',
    rowAccent: 'border-l-sky-400 dark:border-l-sky-500'
  },
  'Soil, Mulch & Org.': {
    headerBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    headerText: 'text-emerald-800 dark:text-emerald-200',
    rowAccent: 'border-l-emerald-400 dark:border-l-emerald-500'
  },
  'Landscape & Decor': {
    headerBg: 'bg-amber-100 dark:bg-amber-900/30',
    headerText: 'text-amber-800 dark:text-amber-200',
    rowAccent: 'border-l-amber-400 dark:border-l-amber-500'
  },
  'Quarry Rock': {
    headerBg: 'bg-slate-200 dark:bg-slate-700/40',
    headerText: 'text-slate-700 dark:text-slate-200',
    rowAccent: 'border-l-slate-400 dark:border-l-slate-500'
  }
};
type ProductInfo = {
  pricePerYard: number;
  slug: string;
  categoryName: string;
};
const productMap: Record<string, ProductInfo> = {};
const materialOptions: {
  value: string;
  label: string;
  disabled?: boolean;
  isHeader?: boolean;
  categoryName?: string;
}[] = [];
categories.forEach(cat => {
  materialOptions.push({
    value: `__cat__${cat.name}`,
    label: cat.name,
    disabled: true,
    isHeader: true,
    categoryName: cat.name
  });
  cat.products.forEach(p => {
    productMap[p.name] = {
      pricePerYard: p.pricePerYard,
      slug: p.slug,
      categoryName: cat.name
    };
    materialOptions.push({
      value: p.name,
      label: `${p.name} — $${p.pricePerYard}/yd`,
      categoryName: cat.name
    });
  });
});
// Lightweight materials (bark, compost, soil, play chips) fit more cubic
// yards per truckload than dense aggregates. Used for full-load sizing
// and small-load surcharge thresholds.
const isLightweight = (productName: string) => {
  return productMap[productName]?.categoryName === 'Soil, Mulch & Org.';
};
// Build a map of category name → { min, max } pricePerYard
const categoryPriceMap: Record<string, {
  min: number;
  max: number;
}> = {};
categories.forEach(cat => {
  const prices = cat.products.map(p => p.pricePerYard);
  categoryPriceMap[cat.name] = {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
});
// Add bark-specific pricing (Fine Bark, Medium Bark, Coarse Bark are all $57/yd).
// Computed dynamically from data/products.ts below so future price changes
// don't require touching this comment again.
const barkProducts = categories.flatMap(cat => cat.products).filter(p => p.name.toLowerCase().includes('bark'));
if (barkProducts.length > 0) {
  const barkPrices = barkProducts.map(p => p.pricePerYard);
  categoryPriceMap['Bark'] = {
    min: Math.min(...barkPrices),
    max: Math.max(...barkPrices)
  };
}
const heroImages = ['https://cdn.magicpatterns.com/uploads/63NRWa6QZfPUMxTtMMK5Wv/file_0000000026f471f5b9d6d2c535161e4f.png', 'https://cdn.magicpatterns.com/uploads/824wZoPcAij7yeGBiywpRR/file_00000000e00c722f85d5870ff5890903.png', 'https://cdn.magicpatterns.com/uploads/p6WQ2rwEhGVV5j3eE1iAyz/file_00000000128c71fd996dc4dabfafdaa5~2.png'];
// Live deliveries counter — base 900 on a fixed anchor date, then adds
// a deterministic 10–20 deliveries per business day, plus partial progress
// for the current working day. Updates every minute.
const DELIVERIES_ANCHOR = new Date('2025-05-01T00:00:00');
const DELIVERIES_BASE = 900;
function seededRand(seed: number) {
  // Simple deterministic hash → [0,1)
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}
function isBusinessDay(d: Date) {
  const day = d.getDay();
  return day >= 1 && day <= 6; // Mon-Sat
}
function workingMinutesInDay(d: Date) {
  const day = d.getDay();
  if (day === 0) return 0;
  if (day === 6) return 8 * 60; // Sat 7-3
  return 11 * 60; // Mon-Fri 6-5
}
function workingMinutesElapsed(d: Date) {
  const day = d.getDay();
  const minutes = d.getHours() * 60 + d.getMinutes();
  if (day === 0) return 0;
  if (day === 6) {
    if (minutes < 7 * 60) return 0;
    if (minutes >= 15 * 60) return 8 * 60;
    return minutes - 7 * 60;
  }
  if (minutes < 6 * 60) return 0;
  if (minutes >= 17 * 60) return 11 * 60;
  return minutes - 6 * 60;
}
function dailyDelivery(d: Date) {
  // 10–20 inclusive, deterministic per date
  const seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  return 10 + Math.floor(seededRand(seed) * 11);
}
function computeDeliveriesCount(now: Date) {
  let total = DELIVERIES_BASE;
  const cursor = new Date(DELIVERIES_ANCHOR);
  cursor.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  while (cursor < today) {
    if (isBusinessDay(cursor)) total += dailyDelivery(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  // Partial progress through today
  if (isBusinessDay(now)) {
    const todayTotal = dailyDelivery(now);
    const elapsed = workingMinutesElapsed(now);
    const fullDay = workingMinutesInDay(now);
    if (fullDay > 0) {
      total += Math.floor(todayTotal * (elapsed / fullDay));
    }
  }
  return total;
}
function useLiveDeliveries() {
  const [count, setCount] = useState(() => computeDeliveriesCount(new Date()));
  useEffect(() => {
    const update = () => setCount(computeDeliveriesCount(new Date()));
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, []);
  return count;
}
// Single source of truth: shared polygon zones used by the Delivery Zones map page.
import { getZoneForPosition } from '../data/deliveryZones';
function getZoneInfo(lat: number, lon: number) {
  const result = getZoneForPosition(lat, lon);
  return {
    zone: result.zone > 0 ? result.zone : null,
    fee: result.fee,
    label: result.zone > 0 ? `${result.price} Delivery` : 'Custom Quote'
  };
}
function ShopByProjectCTA({
  isDarkText = false
}: {
  isDarkText?: boolean;
}) {
  const t = useT();
  const {
    locale
  } = useLocale();
  // Locale-aware project list — names and taglines flip with the active
  // language, slugs stay constant so the destination route is unchanged.
  const projects = getProjectsForLocale(locale);
  // First project's slug — clicking "Shop by project" deep-links into the
  // projects hub with the first project pre-opened. The project page
  // shows the full set of tabs across the top so users can switch from
  // there. Dedupe in case the data file holds locale duplicates.
  const firstProjectSlug = Array.from(new Map(projects.map(p => [p.slug, p])).values())[0]?.slug || 'new-gravel-driveway';
  const handleCatalogClick = () => {
    const el = document.getElementById('materials');
    if (el) {
      const navHeight = document.querySelector('nav')?.offsetHeight || 80;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }
  };
  return <div className="flex flex-col items-center md:items-start w-full gap-3" data-id="element-835">
      {/* Side-by-side CTAs at 16px — "Shop by project" first as the
                                                                     guided path that deep-links into the projects hub, then a thin
                                                                     vertical separator, then the catalog link as the secondary action. */}
      <div className="flex items-center gap-4" data-id="element-836">
        <motion.span animate={!isDarkText ? {
        color: ['rgba(255,255,255,0.7)', 'rgba(255,255,255,1)', 'rgba(255,255,255,0.7)']
      } : undefined} transition={!isDarkText ? {
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut'
      } : undefined} data-id="element-837">
          <Link to={`/projects/${firstProjectSlug}`} className={`group inline-flex items-center font-medium transition-colors ${isDarkText ? 'text-stone-700 dark:text-white/70 hover:text-[#059F40] dark:hover:text-[#059F40]' : ''}`} data-id="element-838">
            <span className="border-b border-current/30 group-hover:border-current pb-0.5 text-[16px]" data-id="element-839">
              {t('hero.estimator.shopByProject')}
            </span>
            <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" data-id="element-840" />
          </Link>
        </motion.span>

        <span className={`h-5 w-px ${isDarkText ? 'bg-stone-300 dark:bg-white/20' : 'bg-white/25'}`} aria-hidden="true" data-id="element-841" />

        <motion.button type="button" onClick={handleCatalogClick} className={`group inline-flex items-center font-medium transition-colors ${isDarkText ? 'text-stone-700 dark:text-white/70 hover:text-[#059F40] dark:hover:text-[#059F40]' : ''}`} animate={!isDarkText ? {
        color: ['rgba(255,255,255,0.7)', 'rgba(255,255,255,1)', 'rgba(255,255,255,0.7)']
      } : undefined} transition={!isDarkText ? {
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 3
      } : undefined} data-id="element-842">
          <span className="border-b border-current/30 group-hover:border-current pb-0.5 text-[16px]" data-id="element-843">
            {t('hero.estimator.browseFullCatalog')}
          </span>
          <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" data-id="element-844" />
        </motion.button>
      </div>
    </div>;
}
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
    isHeader?: boolean;
    categoryName?: string;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // When the dropdown opens, lift the page so the trigger is just
  // below the sticky nav. This guarantees the user always sees the
  // top of the dropdown after opening it, regardless of where the
  // estimator card was sitting in the viewport.
  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => {
      const card = dropdownRef.current?.closest('.estimator-card') as HTMLElement | null;
      if (card) {
        const navHeight = 72; // top sticky 253GRAVEL nav, approx.
        const targetY = window.scrollY + card.getBoundingClientRect().top - navHeight;
        window.scrollTo({
          top: Math.max(0, targetY),
          behavior: 'smooth'
        });
      } else {
        dropdownRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 50);
    return () => window.clearTimeout(t);
  }, [isOpen]);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-845">
      <button ref={buttonRef} type="button" onClick={() => setIsOpen(!isOpen)} className={`estimator-input w-full px-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059F40] transition-all flex items-center justify-between ${value ? 'text-stone-900 dark:text-white' : 'text-stone-500 dark:text-white/50'}`} data-id="element-846">
        <span className="truncate" data-id="element-847">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-848" />
      </button>

      <AnimatePresence data-id="element-849">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full mt-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-white/10 rounded-lg shadow-xl overflow-y-scroll overscroll-contain touch-pan-y pb-[30px]" style={{
        // Fixed 350px height per spec. Internal scroll engages
        // for option lists taller than this; the pb-[30px] on
        // the container gives the user 30px of breathing room
        // past the last item when scrolled to the bottom.
        height: '300px',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-850">
            {options.map(opt => {
          const colors = opt.categoryName ? categoryColors[opt.categoryName] : null;
          if (opt.isHeader && colors) {
            return <div key={opt.value} className={`px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.12em] ${colors.headerBg} ${colors.headerText} border-t border-stone-200 dark:border-white/5 first:border-t-0 select-none`} data-id="element-851">
                    {opt.label}
                  </div>;
          }
          return <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
            if (!opt.disabled) {
              onChange(opt.value);
              setIsOpen(false);
            }
          }} className={`w-full text-left pl-5 pr-4 py-3 text-sm border-l-4 transition-colors ${colors ? colors.rowAccent : 'border-l-transparent'} ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-[#059F40]/20 text-[#059F40] font-semibold' : 'text-stone-900 dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-852">
                  {opt.label}
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Hero() {
  const estimatorRef = useRef<HTMLDivElement>(null);
  const quoteResultRef = useRef<HTMLDivElement>(null);
  const {
    addToCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  // Rotating reassurance message above the estimator — swaps every 3s
  // between "Pay on delivery" trust copy and "30 min from your driveway"
  // proximity copy. Folds the freestanding green pill below the estimator
  // into a single high-attention spot at the top.
  const [rotatingMsgIndex, setRotatingMsgIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setRotatingMsgIndex(i => (i + 1) % 2);
    }, 3000);
    return () => clearInterval(id);
  }, []);
  const [address, setAddress] = useState('');
  const [material, setMaterial] = useState('');
  // Most orders are full loads — preselect 12yd so users only need to confirm.
  // Switches automatically to 15 below if a lightweight material is chosen.
  const [quantity, setQuantity] = useState('12');
  const [numLoads, setNumLoads] = useState('');
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteResult, setQuoteResult] = useState<{
    priceLow: number;
    priceHigh: number;
    address: string;
    quantity: string;
    material: string;
    materialSlug?: string;
    deliveryZone: number | null;
    deliveryFee: number;
    deliveryLabel: string;
    smallLoadSurcharge: number;
    effectivePricePerYardLow: number;
    effectivePricePerYardHigh: number;
    basePricePerYardLow: number;
    basePricePerYardHigh: number;
    deliveries?: number;
    bulkDiscount?: number;
    // When a custom yardage is split across loads, the last load may fall
    // below the 5yd per-load minimum and gets billed up to 5yd. This field
    // captures that adjustment so we can surface a transparent warning.
    minLoadAdjustment?: {
      requested: number;
      billable: number;
      lastLoadActual: number;
    } | null;
  } | null>(null);
  // Toggle for the "why am I billed for more yards?" info popover.
  const [showMinLoadInfo, setShowMinLoadInfo] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [heroImage] = useState(() => {
    try {
      const key = 'hero_image_index';
      const stored = parseInt(sessionStorage.getItem(key) || '0', 10);
      const index = stored % heroImages.length;
      sessionStorage.setItem(key, String(index + 1));
      return heroImages[index];
    } catch {
      return heroImages[0];
    }
  });
  const [showEstimator, setShowEstimator] = useState(true);
  // Address-first flow: address is the primary, always-visible input.
  // Once it geocodes successfully, material + quantity row reveals.
  const [addressZone, setAddressZone] = useState<{
    zone: number | null;
    fee: number;
    label: string;
  } | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [showMaterialRow, setShowMaterialRow] = useState(false);
  // True when the user explicitly chose "Continue anyway" after a failed
  // geocode. Quote is generated without a delivery fee; dispatch confirms
  // the address (and final fee) by phone before the truck rolls.
  const [addressUnverified, setAddressUnverified] = useState(false);
  // Custom yardage produced by the inline calculator (allows non-standard
  // single-trip quantities like 13yd, which auto-split across deliveries
  // in pricing math but display as a single line item in the quantity menu).
  const [customQty, setCustomQty] = useState<number | null>(null);
  // Manual yards input for the "More than 1 load" branch — lets users type
  // an exact yardage without going through the calculator.
  const [manualYards, setManualYards] = useState('');
  // Inline "Not sure?" calculator state for the How Much step
  const [showHeroCalc, setShowHeroCalc] = useState(false);
  const [heroCalcShape, setHeroCalcShape] = useState<'rectangle' | 'circle' | 'triangle' | 'irregular'>('rectangle');
  const [heroCalcLength, setHeroCalcLength] = useState('');
  const [heroCalcWidth, setHeroCalcWidth] = useState('');
  const [heroCalcDiameter, setHeroCalcDiameter] = useState('');
  const [heroCalcBase, setHeroCalcBase] = useState('');
  const [heroCalcHeight, setHeroCalcHeight] = useState('');
  const [heroCalcDepth, setHeroCalcDepth] = useState('3');
  const [showProjectsPanel, setShowProjectsPanel] = useState(false);
  const t = useT();
  const {
    locale
  } = useLocale();
  const liveDeliveries = useLiveDeliveries();
  const [searchParams, setSearchParams] = useSearchParams();
  // Build a "return to estimator" query string that preserves the user's
  // current estimator state (address, material, yardage). Used on every
  // outbound Link to /delivery-zones so the map page can offer a one-tap
  // "Back to Estimate" button — and Hero's mount-effect (below) will
  // re-hydrate the form from these same params on return.
  const estimatorReturnParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('from', 'estimator');
    if (address) params.set('address', address);
    if (material) params.set('material', material);
    const light = material && productMap[material] ? isLightweight(material) : false;
    const fullLoad = light ? 15 : 12;
    let yards = 0;
    if (quantity === 'multi') {
      const m = parseInt(manualYards, 10);
      if (!isNaN(m) && m > 0) {
        yards = m;
      } else if (numLoads === '3+') {
        yards = 3 * fullLoad;
      } else if (numLoads) {
        const n = parseInt(numLoads, 10);
        if (!isNaN(n)) yards = n * fullLoad;
      }
    } else if (customQty && customQty > 0) {
      yards = customQty;
    } else if (quantity) {
      const n = parseInt(quantity, 10);
      if (!isNaN(n) && n > 0) yards = n;
    }
    if (yards > 0) params.set('qty', String(yards));
    return params.toString();
  }, [address, material, quantity, numLoads, manualYards, customQty]);
  // Return flow from Help Me Choose calculator: when the user comes back
  // with ?qty=N (and optionally &material=NAME&address=...), pre-fill the
  // estimator and scroll it into view so they can hit Get Estimate.
  // - qty ≤ full load → snap quantity dropdown to that single-load value
  // - qty > full load → switch to "Multiple loads" with the right load count
  useEffect(() => {
    const qtyParam = searchParams.get('qty');
    const materialParam = searchParams.get('material');
    const addressParam = searchParams.get('address');
    if (!qtyParam && !materialParam && !addressParam) return;
    if (addressParam) setAddress(addressParam);
    if (materialParam && productMap[materialParam]) {
      setMaterial(materialParam);
    }
    if (qtyParam) {
      const n = parseInt(qtyParam, 10);
      if (!isNaN(n) && n >= 1) {
        // Determine full-load size from material if we have it, else assume
        // heavy (12 yd) — conservative default that splits more aggressively.
        const light = materialParam && productMap[materialParam] ? isLightweight(materialParam) : false;
        const fullLoad = light ? 15 : 12;
        if (n <= fullLoad) {
          // Snap to nearest valid single-load dropdown value (3..11 or fullLoad).
          const snapped = Math.max(3, Math.min(fullLoad, n));
          setQuantity(String(snapped));
          setNumLoads('');
        } else {
          // Multi-load: pick the load count that covers the requested yardage.
          const loads = Math.ceil(n / fullLoad);
          setQuantity('multi');
          setNumLoads(loads >= 3 ? '3+' : String(loads));
        }
      }
    }
    // Clear the URL params after consuming them so a refresh doesn't
    // re-trigger this effect.
    const next = new URLSearchParams(searchParams);
    next.delete('qty');
    next.delete('material');
    next.delete('address');
    setSearchParams(next, {
      replace: true
    });
    // Scroll the estimator into view so the user lands on the pre-filled form.
    setTimeout(() => {
      estimatorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Seasonal messaging — rotates between 2 messages based on season, cycling on refresh
  const springSummerMessages = [{
    headlineLine1: 'Spring Into Action.',
    headlineLine2: 'Fresh Starts Begin Here.',
    subtitle: 'The smartest way to order topsoil, bark mulch, and garden prep materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Build Season Is Here.',
    headlineLine2: 'Your Project Starts Now.',
    subtitle: 'The smartest way to order materials for driveways, patios, and landscaping — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: "Sun's Out, Trucks Out.",
    headlineLine2: "Let's Get It Done.",
    subtitle: 'The smartest way to order gravel, topsoil, and landscaping materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: "Your Yard Won't Wait.",
    headlineLine2: 'Neither Will We.',
    subtitle: 'The smartest way to order bark, compost, and garden-ready materials — instant estimates and automatic bulk pricing from your phone.'
  }];
  const fallWinterMessages = [{
    headlineLine1: 'Prep Before the Rain.',
    headlineLine2: 'Fall Cleanup Made Easy.',
    subtitle: 'The smartest way to order drain rock, bark mulch, and grading materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'One Thumb.',
    headlineLine2: 'Full Driveway.',
    subtitle: 'The smartest way to order gravel, drainage, and winter prep materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Beat the Freeze.',
    headlineLine2: 'Gravel Up Before Winter.',
    subtitle: 'The smartest way to order crushed rock, drain rock, and fill materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Mud Season? Handled.',
    headlineLine2: 'Solid Ground Starts Here.',
    subtitle: 'The smartest way to order base rock, gravel, and drainage materials — instant estimates and automatic bulk pricing from your phone.'
  }];
  const [seasonalContent] = useState(() => {
    try {
      const month = new Date().getMonth(); // 0-11
      const isWarmSeason = month >= 2 && month <= 7; // Mar-Aug
      const pool = isWarmSeason ? springSummerMessages : fallWinterMessages;
      const key = 'hero_seasonal_index';
      const stored = parseInt(sessionStorage.getItem(key) || '0', 10);
      const index = stored % pool.length;
      sessionStorage.setItem(key, String(stored + 1));
      return pool[index];
    } catch {
      return fallWinterMessages[1];
    }
  });
  // Debounced address geocoding — runs as user types. Once we get a valid zone,
  // we reveal the material + quantity row.
  useEffect(() => {
    if (!address.trim()) {
      setAddressZone(null);
      setAddressError('');
      setShowMaterialRow(false);
      setAddressUnverified(false);
      return;
    }
    if (addressUnverified) setAddressUnverified(false);
    const timer = setTimeout(async () => {
      setIsGeocoding(true);
      setAddressError('');
      try {
        // Only append ", WA" if the address doesn't already include the state —
        // doubling the state reference can cause Nominatim to return no results
        // for otherwise valid addresses (e.g. "...Seattle, WA 98107").
        const alreadyHasWA = /\b(WA|Washington)\b/i.test(address);
        const query = encodeURIComponent(alreadyHasWA ? address : address + ', WA');
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Try a more specific address.');
          setAddressZone(null);
          setShowMaterialRow(false);
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const info = getZoneInfo(lat, lon);
          setAddressZone(info);
          setShowMaterialRow(true);
        }
      } catch {
        setAddressError('Could not verify address. Please try again.');
        setShowMaterialRow(false);
      } finally {
        setIsGeocoding(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [address]);
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard against pressing Enter in the address field before a material
    // has been picked. The visible "Get Instant Estimate" button is gated
    // on address + material + quantity, but native form submit (Enter key)
    // would bypass that gate and produce a ghost quote using the fallback
    // $55/yd default — confusing the customer with a price for a material
    // they never selected.
    if (!address) return;
    if (!material || !productMap[material]) return;
    if (!quantity) return;
    if (quantity === 'multi' && !numLoads) return;
    setIsSearching(true);
    setQuoteResult(null);
    if (addressUnverified) {
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '3+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          deliveries = Math.ceil(qty / fullLoadYards);
        } else {
          const threshold = light ? 12 : 10;
          isSmallLoad = qty < threshold;
          if (qty < threshold) {
            surchargeMultiplier = 1 + (threshold - qty) * 0.1;
          }
        }
      }
      const MIN_LOAD = 3;
      let billableQty = qty;
      let minAdjust: {
        requested: number;
        billable: number;
        lastLoadActual: number;
      } | null = null;
      if (quantity !== 'multi' && deliveries === 1 && qty < MIN_LOAD) {
        billableQty = MIN_LOAD;
        minAdjust = {
          requested: qty,
          billable: MIN_LOAD,
          lastLoadActual: qty
        };
      }
      const bulkPctU = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      let materialsTotal = 0;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        const threshold = light ? 12 : 10;
        let lastLoadSurcharge = 1;
        if (lastLoad < threshold) {
          lastLoadSurcharge = 1 + (threshold - lastLoad) * 0.1;
        }
        const fullLoadPrice = Math.ceil(basePrice * (1 - bulkPctU));
        const partialPrice = Math.ceil(basePrice * lastLoadSurcharge * (1 - bulkPctU));
        materialsTotal = (deliveries - 1) * fullLoadYards * fullLoadPrice + lastLoad * partialPrice;
      } else {
        const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPctU));
        materialsTotal = effective * billableQty;
      }
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPctU));
      setQuoteResult({
        priceLow: materialsTotal,
        priceHigh: materialsTotal,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: null,
        deliveryFee: 0,
        deliveryLabel: "Delivery fee TBD — we'll confirm before dispatch",
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPctU,
        minLoadAdjustment: minAdjust
      });
      setIsSearching(false);
      setTimeout(() => {
        if (quoteResultRef.current) {
          const navHeight = document.querySelector('nav')?.offsetHeight || 80;
          const top = quoteResultRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 12;
          window.scrollTo({
            top: Math.max(0, top),
            behavior: 'smooth'
          });
        }
      }, 250);
      return;
    }
    try {
      // Geocode the address — only append ", WA" if the state isn't already
      // in the user's input, to avoid Nominatim zero-result quirks on doubled
      // state references like "...Seattle, WA 98107".
      const alreadyHasWA = /\b(WA|Washington)\b/i.test(address);
      const query = encodeURIComponent(alreadyHasWA ? address : address + ', WA');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
      const data = await res.json();
      let zoneInfo = {
        zone: null as number | null,
        fee: 0,
        label: 'Custom Quote'
      };
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        zoneInfo = getZoneInfo(lat, lon);
      }
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '3+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          // Custom amount from calculator that exceeds one truckload —
          // auto-split into the right number of deliveries. No small-load
          // surcharge applies since at least one truck is full.
          deliveries = Math.ceil(qty / fullLoadYards);
          isSmallLoad = false;
          surchargeMultiplier = 1;
        } else {
          // Single-load small-order surcharge. Threshold is 12 yd for
          // light material, 10 yd for heavy. Sub-5yd uses a steeper
          // 15%/yd curve to protect margin on the smallest orders.
          const threshold = light ? 12 : 10;
          isSmallLoad = qty < threshold;
          if (qty >= threshold) {
            surchargeMultiplier = 1;
          } else {
            surchargeMultiplier = 1 + (threshold - qty) * 0.1;
          }
        }
      }
      // Standalone single-delivery orders below 3 yd are bumped to the 3
      // yd order minimum. Partial last loads in a multi-delivery split are
      // NOT bumped — they're billed at actual yardage with the small-load
      // surcharge applied per the cart pricing rule.
      const MIN_LOAD = 3;
      let billableQty = qty;
      let minAdjust: {
        requested: number;
        billable: number;
        lastLoadActual: number;
      } | null = null;
      if (quantity !== 'multi' && deliveries === 1 && qty < MIN_LOAD) {
        billableQty = MIN_LOAD;
        minAdjust = {
          requested: qty,
          billable: MIN_LOAD,
          lastLoadActual: qty
        };
      }
      const bulkPct = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      // Pricing math: when there's a partial last load, only the partial
      // gets the small-load surcharge. Full loads bill at base price.
      let materialsTotal = 0;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        const threshold = light ? 12 : 10;
        let lastLoadSurcharge = 1;
        if (lastLoad < threshold) {
          lastLoadSurcharge = 1 + (threshold - lastLoad) * 0.1;
        }
        const fullLoadPrice = Math.ceil(basePrice * (1 - bulkPct));
        const partialPrice = Math.ceil(basePrice * lastLoadSurcharge * (1 - bulkPct));
        materialsTotal = (deliveries - 1) * fullLoadYards * fullLoadPrice + lastLoad * partialPrice;
      } else {
        const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct));
        materialsTotal = effective * billableQty;
      }
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct));
      const total = materialsTotal + zoneInfo.fee * deliveries;
      setQuoteResult({
        priceLow: total,
        priceHigh: total,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: zoneInfo.zone,
        deliveryFee: zoneInfo.fee * deliveries,
        deliveryLabel: zoneInfo.label,
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPct,
        minLoadAdjustment: minAdjust
      });
    } catch {
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '3+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          deliveries = Math.ceil(qty / fullLoadYards);
          isSmallLoad = false;
          surchargeMultiplier = 1;
        } else {
          const threshold = light ? 12 : 10;
          isSmallLoad = qty < threshold;
          if (qty >= threshold) {
            surchargeMultiplier = 1;
          } else {
            surchargeMultiplier = 1 + (threshold - qty) * 0.1;
          }
        }
      }
      const MIN_LOAD = 3;
      let billableQty = qty;
      let minAdjust: {
        requested: number;
        billable: number;
        lastLoadActual: number;
      } | null = null;
      if (quantity !== 'multi' && deliveries === 1 && qty < MIN_LOAD) {
        billableQty = MIN_LOAD;
        minAdjust = {
          requested: qty,
          billable: MIN_LOAD,
          lastLoadActual: qty
        };
      }
      const bulkPct2 = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      let materialsTotal = 0;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        const threshold = light ? 12 : 10;
        let lastLoadSurcharge = 1;
        if (lastLoad < threshold) {
          lastLoadSurcharge = 1 + (threshold - lastLoad) * 0.1;
        }
        const fullLoadPrice = Math.ceil(basePrice * (1 - bulkPct2));
        const partialPrice = Math.ceil(basePrice * lastLoadSurcharge * (1 - bulkPct2));
        materialsTotal = (deliveries - 1) * fullLoadYards * fullLoadPrice + lastLoad * partialPrice;
      } else {
        const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct2));
        materialsTotal = effective * billableQty;
      }
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct2));
      const total = materialsTotal + 100 * deliveries;
      setQuoteResult({
        priceLow: total,
        priceHigh: total,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: null,
        deliveryFee: 0,
        deliveryLabel: 'Custom Quote',
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPct2,
        minLoadAdjustment: minAdjust
      });
    }
    setIsSearching(false);
    setTimeout(() => {
      if (quoteResultRef.current) {
        const navHeight = document.querySelector('nav')?.offsetHeight || 80;
        const top = quoteResultRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      }
    }, 250);
  };
  const handleCloseEstimator = () => {
    setShowEstimator(false);
    setShowMaterialRow(false);
    setMaterial('');
    setQuantity('12');
    setNumLoads('');
    setQuoteResult(null);
    setAddress('');
    setAddressZone(null);
    setAddressError('');
  };
  // One-tap: add the quoted material to the cart, persist delivery info, open cart drawer.
  // Lets a finished estimator flow straight into checkout without re-entering address.
  // For multi-load orders, split into separate cart line items so the user sees
  // a clear breakdown (and the small-load surcharge applies correctly to the
  // partial last load).
  const handleAddToTruckFromQuote = () => {
    if (!quoteResult) return;
    const productInfo = productMap[quoteResult.material];
    if (!productInfo) return;
    // For multi-delivery orders the billed quantity equals what the
    // customer asked for (partial last loads no longer get bumped). For a
    // standalone <5 yd single delivery we still want to add 5 yd to the
    // cart (billing minimum) and flag requestedQuantity so the cart can
    // show the transparency note.
    const finalQuantity = parseInt(quoteResult.quantity) || quoteResult.minLoadAdjustment?.billable || 0;
    if (finalQuantity <= 0) return;
    const deliveries = quoteResult.deliveries || 1;
    const light = isLightweight(quoteResult.material);
    const fullLoad = light ? 15 : 12;
    const MIN_LOAD = 3;
    if (deliveries > 1) {
      // Split across N deliveries: full loads for all but last, actual
      // remainder for the last. Each load is its own cart line item so the
      // user sees the breakdown and the small-load surcharge applies
      // naturally to a small partial last load (no 5 yd minimum bump on
      // partial loads — customer is already paying for at least one full
      // load).
      const firstLoadsTotal = (deliveries - 1) * fullLoad;
      const lastLoad = finalQuantity - firstLoadsTotal;
      for (let i = 0; i < deliveries; i++) {
        const isLast = i === deliveries - 1;
        const qty = isLast ? lastLoad : fullLoad;
        addToCart({
          category: productInfo.categoryName,
          product: `${quoteResult.material} (Delivery ${i + 1})`,
          quantity: qty,
          pricePerYard: productInfo.pricePerYard,
          source: 'estimator'
        });
      }
    } else {
      // Standalone single-delivery: flag requestedQuantity when we bumped
      // a <5 yd order up to the 5 yd order minimum so the cart can render
      // the transparency note.
      const adj = quoteResult.minLoadAdjustment;
      const requestedQuantity = adj && adj.requested > 0 && adj.requested < finalQuantity ? adj.requested : undefined;
      addToCart({
        category: productInfo.categoryName,
        product: quoteResult.material,
        quantity: finalQuantity,
        pricePerYard: productInfo.pricePerYard,
        source: 'estimator',
        requestedQuantity
      });
    }
    // Persist the address + zone + fee so CartDrawer doesn't ask again.
    setDeliveryInfo(quoteResult.address, quoteResult.deliveryFee && quoteResult.deliveries ? Math.round(quoteResult.deliveryFee / quoteResult.deliveries) : 0, quoteResult.deliveryZone);
    openCart();
  };
  return <section className="relative w-full bg-stone-200 dark:bg-[#2a2a2a] py-14 md:py-40 overflow-hidden" style={{
    marginTop: '-149px',
    paddingTop: 'calc(149px + 3.5rem)'
  }} data-id="element-853">
      {/* Background Image - extends behind navbar */}
      <motion.div className="absolute inset-0 z-0 bg-cover bg-center" style={{
      backgroundImage: `url(${heroImage})`,
      top: 0
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1.5,
      ease: 'easeInOut'
    }} data-id="element-854" />
      {/* Black overlays — same in light & dark mode */}
      <div className="absolute inset-0 z-0 md:hidden bg-black/50" data-id="element-855" />
      <div className="absolute inset-0 z-0 hidden md:block bg-gradient-to-r from-black/70 via-black/40 to-black/10" data-id="element-856" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-857">
        {/* Personalized greeting for returning visitors — only renders if a
            saved last-order exists in localStorage and the user hasn't
            dismissed it this session. */}
        <WelcomeBackCard data-id="element-858" />

        <div className="text-center md:text-left mb-8 md:mb-12 max-w-3xl" data-id="element-859">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} className="flex items-center justify-center md:justify-start mb-4 md:mb-6" data-id="element-860">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-none bg-stone-900/10 dark:bg-white/10 backdrop-blur-sm border border-stone-900/15 dark:border-white/15 text-stone-800 dark:text-white/90 text-xs sm:text-sm font-semibold tracking-widest uppercase" data-id="element-861">
              <span className="w-1.5 h-1.5 rounded-full bg-[#059F40] animate-pulse" data-id="element-862" />
              Order Gravel Like It's 2026
            </span>
          </motion.div>

          <AnimatePresence mode="wait" data-id="element-863">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.1
          }} data-id="element-864">
              <div className="w-12 md:w-16 h-1 md:h-1.5 bg-[#059F40] mb-4 md:mb-6 mx-auto md:mx-0 rounded-full" data-id="element-865" />
              <AnimatePresence mode="wait" data-id="element-866">
                <motion.h1 key={locale + seasonalContent.headlineLine1} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3
              }} className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 md:mb-6 text-stone-900 dark:text-white leading-[1.1]" data-id="element-867">
                  {locale === 'es' ? t('hero.headlineLine1') : seasonalContent.headlineLine1}
                  <span className="block mt-1 md:mt-2 text-[#059F40]" data-id="element-868">
                    {locale === 'es' ? t('hero.headlineLine2') : seasonalContent.headlineLine2}
                  </span>
                </motion.h1>
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait" data-id="element-869">
            <motion.p key={locale + seasonalContent.subtitle} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3,
            delay: 0.1
          }} className="text-lg md:text-xl text-stone-700 dark:text-white/90 font-semibold font-sub mb-5 md:mb-6 max-w-2xl mx-auto md:mx-0" data-id="element-870">
              {locale === 'es' ? t('hero.subtitle') : seasonalContent.subtitle}
            </motion.p>
          </AnimatePresence>

          {/* Review Badge + Feature Pills */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.25
        }} className="mb-3 md:mb-5 space-y-2" data-id="element-871">
            <div className="flex items-center gap-2 justify-center md:justify-start" data-id="element-872">
              <div className="flex items-center gap-1.5 bg-stone-900/10 dark:bg-white/10 backdrop-blur-md border border-stone-900/15 dark:border-white/20 rounded-full px-4 py-2" data-id="element-873">
                <div className="flex" data-id="element-874">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-875" />)}
                </div>
                <span className="text-stone-400 dark:text-white/50 text-sm mx-1" data-id="element-876">
                  ·
                </span>
                <span className="text-stone-700 dark:text-white/80 text-sm font-medium tabular-nums" data-id="element-877">
                  {liveDeliveries.toLocaleString()}+ {t('hero.deliveriesWord')}
                </span>
              </div>
            </div>
            {/* Feature pills hide when estimator is open to reduce clutter */}
            <AnimatePresence data-id="element-878">
              {!showEstimator && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.25
            }} className="flex flex-wrap gap-2 justify-center md:justify-start overflow-hidden" data-id="element-879">
                  {[{
                icon: '⚡',
                label: t('hero.feat.estimates')
              }, {
                icon: '🧮',
                label: t('hero.feat.bulk')
              }, {
                icon: '📊',
                label: t('hero.feat.compare')
              }].map(pill => <span key={pill.label} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs sm:text-sm font-medium backdrop-blur-sm border ${pill.highlight ? 'bg-[#059F40]/15 dark:bg-[#059F40]/15 border-[#059F40]/40 dark:border-[#059F40]/50 text-stone-900 dark:text-white' : 'bg-stone-900/5 dark:bg-white/5 border-stone-900/10 dark:border-white/10 text-stone-700 dark:text-white/80'}`} data-id="element-880">
                      <span data-id="element-881">{pill.icon}</span>
                      {pill.label}
                    </span>)}
                </motion.div>}
            </AnimatePresence>
          </motion.div>

          {/* CTA Buttons */}
          {!showEstimator && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.3
        }} className="flex flex-col items-center md:items-start gap-3" data-id="element-882">
              <button onClick={() => {
            setShowEstimator(true);
            setTimeout(() => {
              if (estimatorRef.current) {
                const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                const top = estimatorRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 80;
                window.scrollTo({
                  top,
                  behavior: 'smooth'
                });
              }
            }, 150);
          }} className="w-full sm:w-auto px-8 py-5 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/25 hover:border-white/40 rounded-xl font-bold text-base sm:text-lg transition-all flex items-center justify-center text-[#05C950]" data-id="element-883">
                {t('hero.getEstimate')}
                <ArrowRight className="w-5 h-5" data-id="element-884" />
              </button>
              <ShopByProjectCTA isDarkText={true} data-id="element-885" />
            </motion.div>}
        </div>

        {/* Lead-in label above the estimator on page load */}
        <AnimatePresence data-id="element-886">
          {showEstimator && <motion.div key="estimator-leadin" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 8
        }} transition={{
          duration: 0.3,
          delay: 0.1
        }} className="mb-4 max-w-3xl mx-auto md:mx-0" data-id="element-887">
              {/* Both messages render simultaneously in the same CSS grid
               cell so the container always reserves the width of the
               widest one — eliminating the layout shift that was nudging
               the estimator on every swap. We only animate opacity. */}
              <div className="grid justify-center md:justify-start" data-id="element-888">
                <motion.div animate={{
              opacity: rotatingMsgIndex === 0 ? 1 : 0
            }} transition={{
              duration: 0.45,
              ease: 'easeInOut'
            }} className="row-start-1 col-start-1 flex items-center gap-2" aria-hidden={rotatingMsgIndex !== 0} data-id="element-889">
                  <CheckCircle2 className="w-4 h-4 text-[#059F40] shrink-0" data-id="element-890" />
                  <span className="text-sm font-medium text-white" data-id="element-891">
                    Pay on delivery, no card needed
                  </span>
                </motion.div>
                <motion.div animate={{
              opacity: rotatingMsgIndex === 1 ? 1 : 0
            }} transition={{
              duration: 0.45,
              ease: 'easeInOut'
            }} className="row-start-1 col-start-1 flex items-center gap-2" aria-hidden={rotatingMsgIndex !== 1} data-id="element-892">
                  <span aria-hidden="true" data-id="element-893">🏠</span>
                  <span className="text-sm font-medium text-white" data-id="element-894">
                    30 Min from Your Driveway
                  </span>
                </motion.div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Estimator Card */}
        <AnimatePresence data-id="element-895">
          {showEstimator && <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} ref={estimatorRef} className="estimator-card relative z-20 rounded-2xl p-6 sm:p-8 mb-10 max-w-3xl mx-auto md:mx-0 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] scroll-mt-[180px] bg-black/40 backdrop-blur-xl border border-white/20" data-id="element-896">
              <button onClick={handleCloseEstimator} className="absolute top-4 right-4 p-2 text-stone-700 dark:text-white/60 hover:text-stone-900 dark:hover:text-white transition-colors" data-id="element-897">
                <X className="w-5 h-5" data-id="element-898" />
              </button>

              <p className="estimator-label text-sm font-bold uppercase tracking-widest mb-4 flex items-center" data-id="element-899">
                <span className="w-8 h-px bg-current opacity-70 mr-3" data-id="element-900"></span>
                {t('hero.estimator.label')}
              </p>

              {/* Returning visitor: slim one-tap reorder chip above the form.
               Renders nothing for first-time visitors. */}
              <div className="mb-4" data-id="element-901">
                <ReorderChip data-id="element-902" />
              </div>

              <form onSubmit={handleSearch} className="space-y-4" data-id="element-903">
                {/* Step 1: Address (always first) */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-2" data-id="element-904">
                  <label className="estimator-label flex items-center text-sm font-bold uppercase tracking-wider gap-1.5" data-id="element-905">
                    <MapPin className="w-3.5 h-3.5 text-[#059F40]" data-id="element-906" />
                    {t('hero.estimator.step1')}
                  </label>
                  <AddressAutocomplete value={address} onChange={next => {
                setAddress(next);
                // If the user edits the address after we've already
                // produced a quote, invalidate the quote — otherwise
                // clicking "Add to Cart" would push the OLD address
                // (the one tied to the prior quote) into the cart
                // instead of what the customer just typed. Forcing
                // a re-quote guarantees the cart and the estimator
                // always agree on the delivery address.
                if (quoteResult && next !== quoteResult.address) {
                  setQuoteResult(null);
                }
              }} onSelect={s => {
                // User picked a suggestion — skip the debounced geocode
                // and act on the coordinates directly.
                const info = getZoneInfo(s.lat, s.lon);
                setAddressZone(info);
                setAddressError('');
                setShowMaterialRow(true);
                // Suggestion-pick is also an address change, so any
                // existing quote is now stale — clear it so the user
                // re-runs the estimate against the new address.
                if (quoteResult && s.fullAddress !== quoteResult.address) {
                  setQuoteResult(null);
                }
              }} placeholder={t('hero.estimator.addressPlaceholder')} required autoComplete="street-address" variant="dark" leftIcon={<MapPin className="h-4 w-4 text-stone-700 dark:text-white/60" data-id="element-908" />} rightIcon={!isGeocoding && addressZone?.zone ? <CheckCircle2 className="w-4 h-4 text-[#059F40]" data-id="element-909" /> : undefined} isLoading={isGeocoding} inputClassName="estimator-input block w-full pl-10 pr-10 py-3.5 rounded-lg text-base text-stone-900 dark:text-white placeholder-stone-500 dark:placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#059F40] transition-all" data-id="element-907" />
                  <AnimatePresence mode="wait" data-id="element-910">
                    {addressError && !isGeocoding && !addressUnverified && <motion.div key="err" initial={{
                  opacity: 0,
                  y: -4,
                  height: 0
                }} animate={{
                  opacity: 1,
                  y: 0,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-911">
                        <div className="mt-1 rounded-lg border border-amber-400/50 bg-amber-400/10 p-3.5" data-id="element-912">
                          <div className="flex items-start gap-2.5" data-id="element-913">
                            <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-914" />
                            <div className="flex-1 min-w-0" data-id="element-915">
                              <p className="text-sm font-bold text-amber-200 leading-snug" data-id="element-916">
                                We couldn't auto-verify this address
                              </p>
                              <p className="mt-1 text-xs text-white/80 leading-relaxed" data-id="element-917">
                                No problem — you can still get a quote for the
                                materials. Our dispatcher will reach out to
                                confirm the delivery address and final fee
                                before your truck is scheduled.
                              </p>
                              <div className="mt-3 flex flex-col sm:flex-row gap-2" data-id="element-918">
                                <a href="tel:206-619-3742" className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-white hover:bg-stone-100 text-stone-900 text-xs font-bold transition-colors" data-id="element-919">
                                  <Phone className="w-3.5 h-3.5 text-[#059F40]" data-id="element-920" />
                                  Call dispatch · (206) 619-3742
                                </a>
                                <button type="button" onClick={() => {
                            setAddressUnverified(true);
                            setAddressError('');
                            setAddressZone(null);
                            setShowMaterialRow(true);
                          }} className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-[#059F40] hover:bg-[#048036] text-white text-xs font-bold transition-colors" data-id="element-921">
                                  Continue anyway
                                  <ArrowRight className="w-3.5 h-3.5" data-id="element-922" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                    {addressUnverified && !isGeocoding && <motion.div key="unverified" initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-xs font-medium" data-id="element-923">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 font-bold" data-id="element-924">
                          <Info className="w-3 h-3" data-id="element-925" />
                          Address pending confirmation
                        </span>
                        <span className="text-stone-700 dark:text-white/70" data-id="element-926">
                          Dispatch will confirm by phone before delivery
                        </span>
                      </motion.div>}
                    {addressZone && addressZone.zone !== null && !isGeocoding && <motion.div key="zone" initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-xs font-medium" data-id="element-927">
                          <Link to={`/delivery-zones?${estimatorReturnParams}`} className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#059F40]/15 border border-[#059F40]/30 text-[#059F40] font-bold hover:bg-[#059F40]/25 hover:border-[#059F40]/50 transition-colors" title="View on delivery zones map" data-id="element-928">
                            {t('hero.quote.zone')} {addressZone.zone}
                            <MapPin className="w-3 h-3 opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-929" />
                          </Link>
                          <span className="text-stone-700 dark:text-white/70" data-id="element-930">
                            {addressZone.fee === 0 ? t('hero.estimator.freeDelivery') : `+${addressZone.fee} ${t('hero.estimator.plusDelivery')}`}
                          </span>
                        </motion.div>}
                    {addressZone && addressZone.zone === null && !isGeocoding && <motion.p key="custom" initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className="text-xs text-brand-orange font-medium flex items-center gap-1.5" data-id="element-931">
                          <Info className="w-3.5 h-3.5" data-id="element-932" />
                          {t('hero.estimator.outsideZones')}
                        </motion.p>}
                  </AnimatePresence>
                </motion.div>

                {/* Step 2: Material */}
                <AnimatePresence data-id="element-933">
                  {showMaterialRow && <motion.div initial={{
                opacity: 0,
                height: 0,
                y: -10
              }} animate={{
                opacity: 1,
                height: 'auto',
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35
              }} className="space-y-2 overflow-visible" data-id="element-934">
                      <label className="estimator-label flex items-center text-sm font-bold uppercase tracking-wider gap-1.5" data-id="element-935">
                        <span className="w-3.5 h-3.5 rounded-full bg-[#059F40]/20 text-[#059F40] text-[10px] font-extrabold flex items-center justify-center" data-id="element-936">
                          2
                        </span>
                        What material?
                      </label>
                      <CustomSelect value={material} onChange={val => {
                  setMaterial(val);
                  // Auto-bump the quantity dropdown to the per-product
                  // default when material changes. Soil/Mulch/Org.
                  // products (except 50/50 Mix) default to 15 yd —
                  // a full light-material truckload and the typical
                  // homeowner spring-project size. Everything else
                  // (and 50/50 Mix) stays on 12 yd.
                  setQuantity(getDefaultQuantity(val));
                }} placeholder="Select Material" options={materialOptions} data-id="element-937" />
                      <p className="text-xs text-stone-600 dark:text-white/60 pt-1 leading-relaxed" data-id="element-938">
                        Not sure what you need?{' '}
                        <Link to={`/help?from=estimator${address ? `&address=${encodeURIComponent(address)}` : ''}`} className="text-[#059F40] font-semibold hover:underline" data-id="element-939">
                          Take the 30-sec material quiz →
                        </Link>
                      </p>
                    </motion.div>}
                </AnimatePresence>

                {/* Step 3: How much — only reveals after a material is picked */}
                <AnimatePresence data-id="element-940">
                  {showMaterialRow && material && <motion.div initial={{
                opacity: 0,
                height: 0,
                y: -10
              }} animate={{
                opacity: 1,
                height: 'auto',
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35,
                delay: 0.05
              }} className="space-y-2 overflow-visible" data-id="element-941">
                      <label className="estimator-label flex items-center text-xs font-bold uppercase tracking-wider gap-1.5" data-id="element-942">
                        <span className="w-3.5 h-3.5 rounded-full bg-[#059F40]/20 text-[#059F40] text-[10px] font-extrabold flex items-center justify-center" data-id="element-943">
                          3
                        </span>
                        How much do you need?
                      </label>
                      {/* Quantity picker — matches Product page and
                       Services hub layout: small yardages in a 3-col
                       grid on top (small → large), Full Load + Multiple
                       50/50 row on the bottom, square buttons. Keeps
                       the existing dark-glass / #006241 active palette
                       so the picker reads cleanly on the estimator card. */}
                      {(() => {
                  const light = isLightweight(material);
                  const fullLoad = light ? 15 : 12;
                  // Reversed (small → large) so the reading order
                  // flows up toward Full Load at the bottom.
                  const lessYards = light ? [3, 6, 9, 12] : [3, 4, 5, 6, 7, 8, 9, 10, 11];
                  const setQty = (val: string) => {
                    setQuantity(val);
                    setCustomQty(null);
                    if (val !== 'multi') setNumLoads('');
                  };
                  // Border lives on the base class so the pill
                  // border NEVER changes on activation. Only the
                  // background tint and text color shift — that
                  // way the border doesn't visually thicken when
                  // a pill becomes active.
                  const baseBtn = 'rounded-none border border-stone-300 dark:border-white/15 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white/40';
                  const inactive = 'estimator-input text-stone-800 dark:text-white hover:bg-white dark:hover:bg-white/10';
                  // Active = dark-green wash + green text. Border
                  // is inherited from baseBtn so it stays 1px and
                  // doesn't change thickness when activating.
                  const active = 'bg-[#006241]/40 text-[#22c95c] dark:text-[#22c95c]';
                  const gridCols = light ? 'grid-cols-4' : 'grid-cols-3';
                  const isMulti = quantity === 'multi';
                  const isFull = quantity === String(fullLoad);
                  return <div className="space-y-2" data-id="element-944">
                            {/* Small-yardage grid on top — uniform pills,
                             single line, matching heights. */}
                            <div className={`grid ${gridCols} gap-2`} data-id="element-945">
                              {lessYards.map(y => {
                        const v = String(y);
                        const isActive = quantity === v;
                        return <button key={v} type="button" onClick={() => setQty(v)} className={`${baseBtn} ${isActive ? active : inactive} px-3 py-3 flex items-center justify-center leading-tight`} title={y === 3 ? '3 yd · Order minimum' : `${y} yd`} data-id="element-946">
                                    <span className="text-base" data-id="element-947">{y} yd</span>
                                  </button>;
                      })}
                            </div>
                            {/* Hero split row — Full Load + Multiple loads
                             share one line directly above the Get Instant
                             Estimate CTA. */}
                            <div className="grid grid-cols-2 gap-2" data-id="element-948">
                              <button type="button" onClick={() => setQty(String(fullLoad))} className={`${baseBtn} ${isFull ? active : inactive} px-3 py-3 flex items-center justify-center leading-tight text-center`} data-id="element-949">
                                <span data-id="element-950">
                                  Full Load · {fullLoad} yd
                                  <span className={`hidden sm:inline ml-1 text-xs font-normal ${isFull ? 'text-[#059F40]/80 dark:text-[#22c95c]/80' : 'text-stone-500 dark:text-white/60'}`} data-id="element-951">
                                    · Most picked
                                  </span>
                                </span>
                              </button>
                              <button type="button" onClick={() => setQty('multi')} className={`${baseBtn} ${isMulti ? active : inactive} px-3 py-3 flex items-center justify-center leading-tight text-center`} data-id="element-952">
                                Multiple · {fullLoad * 2}+ yd
                              </button>
                            </div>
                            {/* "How many loads?" drops down directly
                             beneath the Multiple pill when active. */}
                            <AnimatePresence initial={false} data-id="element-953">
                              {isMulti && <motion.div key="multi-selector" initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-visible" data-id="element-954">
                                  <div className="pt-1" data-id="element-955">
                                    <CustomSelect value={numLoads} onChange={val => setNumLoads(val)} placeholder="How many loads?" options={[{
                            value: '2',
                            label: '2 Loads'
                          }, {
                            value: '3',
                            label: '3 Loads'
                          }, {
                            value: '3+',
                            label: '3+ Loads (Custom Quote)'
                          }]} data-id="element-956" />
                                    <div className="flex items-start gap-2 p-3 bg-[#059F40]/10 border border-[#059F40]/20 rounded-none mt-3" data-id="element-957">
                                      <Info className="w-4 h-4 text-[#059F40] mt-0.5 shrink-0" data-id="element-958" />
                                      <p className="text-xs text-stone-700 dark:text-white/90" data-id="element-959">
                                        Multi-load orders require separate
                                        deliveries. You will be charged a
                                        delivery fee per load.
                                      </p>
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>
                          </div>;
                })()}
                    </motion.div>}
                </AnimatePresence>

                {/* Submit button — only when address + material + quantity are ready */}
                <AnimatePresence data-id="element-960">
                  {showMaterialRow && material && quantity && (quantity !== 'multi' || numLoads) && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-961">
                        <button type="submit" disabled={isSearching || !address} className="w-full bg-[#059F40] hover:bg-[#059F40] text-white px-6 py-3.5 rounded-lg font-bold text-base transition-colors flex items-center justify-center disabled:opacity-70 shadow-[0_0_20px_rgba(5,159,64,0.25)]" data-id="element-962">
                          {isSearching ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-963"></div> : <>
                              Get Instant Estimate
                              <ArrowRight className="w-4 h-4 ml-2" data-id="element-964" />
                            </>}
                        </button>
                      </motion.div>}
                </AnimatePresence>
              </form>

              <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-xs text-stone-700 dark:text-white/60" data-id="element-965">
                <div className="flex items-center" data-id="element-966">
                  <Lock className="w-3 h-3 mr-1.5 opacity-70" data-id="element-967" />
                  Your information is secure
                </div>
                <span className="hidden sm:inline opacity-30" data-id="element-968">|</span>
                {(() => {
              // Compute current Pacific time + day. Dispatch hours:
              // Mon–Fri 5am–6pm, Sat 7am–3pm, Sun closed.
              const nowPT = new Date(new Date().toLocaleString('en-US', {
                timeZone: 'America/Los_Angeles'
              }));
              const day = nowPT.getDay(); // 0=Sun, 6=Sat
              const hour = nowPT.getHours();
              let isActive = false;
              let label = '';
              if (day === 0) {
                // Sunday — closed all day
                label = 'Dispatch Closed Sundays';
              } else if (day === 6) {
                isActive = hour >= 7 && hour < 15;
                label = isActive ? 'Dispatch Active' : 'Dispatch Opens 7 AM';
              } else {
                isActive = hour >= 5 && hour < 18;
                label = isActive ? 'Dispatch Active' : day === 5 && hour >= 18 ? 'Dispatch Opens Sat 7 AM' : 'Dispatch Opens 5 AM';
              }
              return <div className={`flex items-center gap-1.5 font-medium ${isActive ? 'text-green-600 dark:text-green-400' : 'text-stone-400 dark:text-white/30'}`} data-id="element-969">
                      <span className="relative flex h-2 w-2" data-id="element-970">
                        {isActive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 dark:bg-green-400 opacity-75" data-id="element-971"></span>}
                        <span className={`relative inline-flex rounded-full h-2 w-2 ${isActive ? 'bg-green-500 dark:bg-green-400' : 'bg-stone-400 dark:bg-white/30'}`} data-id="element-972"></span>
                      </span>
                      {label}
                    </div>;
            })()}
              </div>

              {/* Quote Result */}
              {quoteResult && <motion.div ref={quoteResultRef} initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-5 pt-5 border-t border-stone-300/40 dark:border-white/10" data-id="element-973">
                  <p className="text-xs text-stone-700 dark:text-white/80 font-bold uppercase tracking-wider mb-3" data-id="element-974">
                    Price Breakdown
                  </p>
                  <p className="text-sm text-stone-800 dark:text-white/90 font-medium mb-4" data-id="element-975">
                    {quoteResult.quantity} yards of{' '}
                    <span className="font-bold" data-id="element-976">{quoteResult.material}</span>
                    {' → '}
                    {quoteResult.address}
                  </p>

                  {/* Line items */}
                  <div className="space-y-2 mb-4" data-id="element-977">
                    <div className="flex justify-between text-sm" data-id="element-978">
                      <span className="text-stone-700 dark:text-white/70" data-id="element-979">
                        Material — ${quoteResult.effectivePricePerYardLow}/yd ×{' '}
                        {quoteResult.quantity} yds
                      </span>
                      <span className="text-stone-900 dark:text-white font-semibold" data-id="element-980">
                        $
                        {quoteResult.effectivePricePerYardLow * parseInt(quoteResult.quantity)}
                      </span>
                    </div>
                    {quoteResult.minLoadAdjustment && <div className="-mt-1 ml-0.5" data-id="element-981">
                        <button type="button" onClick={() => setShowMinLoadInfo(v => !v)} className="inline-flex items-center gap-1.5 text-xs text-amber-500 dark:text-amber-400 font-medium hover:underline" aria-expanded={showMinLoadInfo} data-id="element-982">
                          <Info className="w-3.5 h-3.5" data-id="element-983" />
                          You asked for{' '}
                          {quoteResult.minLoadAdjustment.requested} yds — billed
                          for {quoteResult.minLoadAdjustment.billable} yds. Why?
                        </button>
                        <AnimatePresence data-id="element-984">
                          {showMinLoadInfo && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-985">
                              <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed space-y-1.5" data-id="element-986">
                                <p data-id="element-987">
                                  <span className="font-bold" data-id="element-988">
                                    3-yard order minimum.
                                  </span>{' '}
                                  You asked for{' '}
                                  {quoteResult.minLoadAdjustment.requested} yd —
                                  billed as 3 yd.
                                </p>
                                <p data-id="element-989">
                                  Trucks have fixed dispatch costs, so any
                                  single delivery is billed at 3 yds even if you
                                  only need a couple. Round up to fill the truck
                                  and you'll get more material at no extra cost.
                                </p>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>}
                    <SmallLoadBreakdown quantity={parseInt(quoteResult.quantity) || 0} productName={quoteResult.material} pricePerYard={quoteResult.basePricePerYardLow} className="-mt-1 ml-0.5" data-id="element-990" />
                    {quoteResult.bulkDiscount && quoteResult.bulkDiscount > 0 ? <p className="text-xs text-[#059F40] -mt-1 ml-0.5" data-id="element-991">
                        {Math.round(quoteResult.bulkDiscount * 100)}% bulk
                        discount applied ({parseInt(quoteResult.quantity)}+
                        yards)
                      </p> : null}
                    <div className="flex justify-between text-sm" data-id="element-992">
                      <span className="text-stone-700 dark:text-white/70" data-id="element-993">
                        Delivery —{' '}
                        {addressUnverified ? 'Confirmed by dispatch' : quoteResult.deliveryZone !== null ? `Zone ${quoteResult.deliveryZone}` : 'Custom Quote'}
                        {quoteResult.deliveries && quoteResult.deliveries > 1 ? ` (×${quoteResult.deliveries})` : ''}
                      </span>
                      <span className={`font-semibold ${addressUnverified ? 'text-amber-400' : 'text-stone-900 dark:text-white'}`} data-id="element-994">
                        {addressUnverified ? 'TBD' : quoteResult.deliveryZone !== null ? `${quoteResult.deliveryFee}` : 'TBD'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-stone-300/40 dark:border-white/10" data-id="element-995">
                      <span className="text-stone-900 dark:text-white font-bold text-sm" data-id="element-996">
                        Estimated Total
                      </span>
                      <span className="text-2xl font-heading font-extrabold text-stone-900 dark:text-white" data-id="element-997">
                        ${quoteResult.priceLow}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4 flex justify-center" data-id="element-998">
                    <SameDayBadge variant="full" data-id="element-999" />
                  </div>

                  <div className="flex flex-col gap-3" data-id="element-1000">
                    <button type="button" onClick={handleAddToTruckFromQuote} className="w-full bg-[#059F40] hover:bg-[#059F40] text-white px-6 py-3.5 rounded-lg font-bold text-base transition-colors flex items-center justify-center shadow-[0_0_20px_rgba(5,159,64,0.25)]" data-id="element-1001">
                      Add to Truck & Check Out
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1002" />
                    </button>
                    <div className={`grid ${quoteResult.materialSlug ? 'grid-cols-2' : 'grid-cols-1'} gap-2.5`} data-id="element-1003">
                      <a href="tel:206-619-3742" className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-stone-300 dark:border-white/15 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 hover:border-[#059F40]/50 dark:hover:border-[#059F40]/50 text-stone-800 dark:text-white text-sm font-semibold transition-all" data-id="element-1004">
                        <Phone className="w-4 h-4 text-[#059F40]" data-id="element-1005" />
                        Call to Order
                      </a>
                      {quoteResult.materialSlug && <Link to={`/shop/${quoteResult.materialSlug}?qty=${encodeURIComponent(
                // Pass the user's REQUESTED yardage (e.g. 14), not
                // the billable value (17 after 5yd-min adjustment).
                // The Product page re-applies the split via
                // buildDeliveryLineItems so the cart shows the same
                // 12 + 5 breakdown the estimator quoted.
                String(quoteResult.minLoadAdjustment?.requested ?? quoteResult.quantity))}`} className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-stone-300 dark:border-white/15 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 hover:border-[#059F40]/50 dark:hover:border-[#059F40]/50 text-stone-800 dark:text-white text-sm font-semibold transition-all" data-id="element-1006">
                          <FileText className="w-4 h-4 text-[#059F40]" data-id="element-1007" />
                          View Material
                        </Link>}
                    </div>
                  </div>

                  {/* Delivery Zone Note */}
                  {addressUnverified ? <div className="mt-4 rounded-lg border border-amber-400/50 bg-amber-400/10 p-3.5" data-id="element-1008">
                      <div className="flex items-start gap-2.5" data-id="element-1009">
                        <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-1010" />
                        <div className="flex-1 min-w-0 text-left" data-id="element-1011">
                          <p className="text-sm font-bold text-amber-200 leading-snug" data-id="element-1012">
                            We'll confirm your address before dispatch
                          </p>
                          <p className="mt-1 text-xs text-white/80 leading-relaxed" data-id="element-1013">
                            This estimate covers the materials only. Once you
                            place the order, dispatch will call you to verify
                            the delivery address and quote the final delivery
                            fee — usually within one business hour.
                          </p>
                          <a href="tel:206-619-3742" className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold text-[#6FE39A] hover:text-white transition-colors" data-id="element-1014">
                            <Phone className="w-3 h-3" data-id="element-1015" />
                            Prefer to call now? (206) 619-3742
                          </a>
                        </div>
                      </div>
                    </div> : quoteResult.deliveryZone !== null ? <p className="mt-4 text-xs text-stone-700 dark:text-white/70 leading-relaxed text-center font-medium" data-id="element-1016">
                      Delivery fee based on your{' '}
                      <Link to={`/delivery-zones?${estimatorReturnParams}`} className="text-[#059F40] hover:underline" data-id="element-1017">
                        delivery zone
                      </Link>
                      .
                    </p> : <div className="mt-3 flex items-center justify-center text-xs text-[#059F40]" data-id="element-1018">
                      <Info className="w-3.5 h-3.5 mr-1.5 shrink-0" data-id="element-1019" />
                      Your address is outside standard zones. Delivery fee may
                      apply —{' '}
                      <Link to={`/delivery-zones?${estimatorReturnParams}`} className="font-semibold ml-1 hover:underline" data-id="element-1020">
                        check delivery zones
                      </Link>
                    </div>}

                  {/* Discreet reset */}
                  <div className="mt-4 flex justify-end" data-id="element-1021">
                    <button onClick={() => {
                handleCloseEstimator();
                setShowEstimator(true);
                setTimeout(() => {
                  if (estimatorRef.current) {
                    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                    const top = estimatorRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 80;
                    window.scrollTo({
                      top,
                      behavior: 'smooth'
                    });
                  }
                }, 150);
              }} className="text-xs text-stone-500 dark:text-white/40 hover:text-stone-800 dark:hover:text-white/70 transition-colors py-1 px-2 -mx-2 rounded" data-id="element-1022">
                      Start over ↻
                    </button>
                  </div>
                </motion.div>}
            </motion.div>}
        </AnimatePresence>

        {/* Catalog escape hatch — sits below the open estimator so the
            browse path stays discoverable without competing with the form. */}
        <AnimatePresence data-id="element-1023">
          {showEstimator && <motion.div key="catalog-link" initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.25
        }} className="-mt-6 mb-8 flex justify-center md:justify-start max-w-3xl mx-auto md:mx-0" data-id="element-1024">
              <ShopByProjectCTA isDarkText={false} data-id="element-1025" />
            </motion.div>}
        </AnimatePresence>

        {/* Trust Badges */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.4
      }} className="flex flex-wrap justify-center md:justify-start gap-x-3 gap-y-1 mt-8" data-id="element-1026">
          <div className="hidden md:flex items-center px-4 py-2 text-sm text-white font-medium" data-id="element-1027">
            <CheckCircle2 className="w-4 h-4 mr-2 text-[#059F40]" data-id="element-1028" />
            {t('hero.trust.fees')}
          </div>
          <div className="hidden md:flex items-center px-4 py-2 text-sm text-white font-medium" data-id="element-1029">
            <CheckCircle2 className="w-4 h-4 mr-2 text-[#059F40]" data-id="element-1030" />
            {t('hero.trust.pay')}
          </div>
        </motion.div>

        <div className="mt-10" data-id="element-1031">
          <LiveDeliveryTicker data-id="element-1032" />
        </div>
      </div>
      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-1033" />
    </section>;
}
export default Hero;